/**
 * Admin — Demo Overlay Funnel (tiny version).
 *
 * Per the product brief: "Do NOT overbuild this". Three numbers that
 * answer one question: "Does the first 10 seconds make teachers act?"
 *
 * No charts. No time-series. No segmentation. Raw funnel counts + the
 * three conversion rates. If the numbers say yes, stop building and
 * start distribution. If they say no, the backlog tells you exactly
 * which lever to pull (landing / film / ending).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowsCounterClockwise } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDemoFunnel() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/admin/demo/stats`);
      setStats(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/admin/demo');
      return;
    }
    fetchStats();
  }, [authLoading, user, navigate]);

  const renderPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
  const targetNote = (key, pct) => {
    if (pct === null || pct === undefined) return null;
    const targets = {
      completion_rate_pct: [60, 80],
      upload_click_rate_pct: [15, 30],
    };
    const t = targets[key];
    if (!t) return null;
    if (pct >= t[0]) return { label: `🎯 target met (${t[0]}–${t[1]}%)`, tone: '#7BB661' };
    return { label: `below target (${t[0]}–${t[1]}%)`, tone: '#C07A4A' };
  };

  return (
    <div
      data-testid="admin-demo-funnel"
      style={{
        minHeight: '100vh',
        background: '#0F1014',
        color: '#fff',
        fontFamily: 'Helvetica, Arial, sans-serif',
        padding: '32px 20px 80px',
      }}
    >
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <button
          onClick={() => navigate('/admin')}
          data-testid="admin-demo-back"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', color: '#9A8B6B',
            fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 20,
            letterSpacing: '0.06em',
          }}
        >
          <ArrowLeft size={12} weight="bold" /> Back to admin
        </button>

        <div style={{
          fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: '#D4AF37', marginBottom: 8,
        }}>
          ✨ Class Film demo funnel
        </div>
        <h1 style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: 36, fontWeight: 400, lineHeight: 1.15, margin: 0,
          marginBottom: 8, letterSpacing: '-0.01em',
        }}>
          Does the first 10 seconds convert?
        </h1>
        <p style={{
          fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6,
          maxWidth: 440, marginTop: 0, marginBottom: 28,
        }}>
          Three numbers. Three questions. If upload-click rate breaks 20%,
          stop building and start distribution.
        </p>

        {loading && (
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}
            data-testid="admin-demo-loading"
          >Loading…</div>
        )}
        {err && (
          <div
            data-testid="admin-demo-error"
            style={{
              padding: '14px 18px', borderRadius: 12,
              background: 'rgba(192,122,74,0.12)',
              border: '1px solid rgba(192,122,74,0.25)',
              color: '#F7D5BD', fontSize: 13,
            }}
          >{err}</div>
        )}

        {stats && !loading && (
          <>
            <div
              data-testid="admin-demo-stats"
              style={{
                display: 'grid', gridTemplateColumns: '1fr', gap: 14,
                marginBottom: 28,
              }}
            >
              <Row
                label="🎬 Demos opened"
                testId="stat-opens"
                value={stats.opens}
                sub="clicked “See a class come to life”"
              />
              <Row
                label="▶️ Completed the film"
                testId="stat-completes"
                value={stats.completes}
                sub={`${renderPct(stats.completion_rate_pct)} of opens`}
                note={targetNote('completion_rate_pct', stats.completion_rate_pct)}
              />
              <Row
                label="📤 Clicked “Upload your photos”"
                testId="stat-uploads"
                value={stats.upload_clicks}
                sub={`${renderPct(stats.upload_click_rate_pct)} of opens · ${renderPct(stats.upload_after_complete_rate_pct)} of completions`}
                note={targetNote('upload_click_rate_pct', stats.upload_click_rate_pct)}
                highlight
              />
            </div>

            <button
              onClick={fetchStats}
              data-testid="admin-demo-refresh"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.75)',
                borderRadius: 999, fontSize: 12,
                cursor: 'pointer', letterSpacing: '0.04em',
              }}
            >
              <ArrowsCounterClockwise size={12} weight="bold" /> Refresh
            </button>

            <div style={{
              marginTop: 36, padding: '18px 20px',
              background: 'rgba(212,175,55,0.06)',
              border: '1px solid rgba(212,175,55,0.18)',
              borderRadius: 14, color: 'rgba(255,255,255,0.78)',
              fontSize: 13, lineHeight: 1.7,
            }}>
              <strong style={{ color: '#D4AF37', letterSpacing: '0.06em' }}>
                What to do with this:
              </strong>
              <ul style={{ margin: '10px 0 0 0', padding: '0 0 0 18px' }}>
                <li><strong>Low opens</strong> → landing headline / CTA problem.</li>
                <li><strong>High opens, low completion</strong> → film problem (pacing, opener, music).</li>
                <li><strong>High completion, low upload click</strong> → ending CTA problem.</li>
                <li><strong>High everything</strong> → stop building, show this to 10 teachers.</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, sub, note, highlight, testId }) => (
  <div
    data-testid={testId}
    style={{
      padding: '18px 22px',
      borderRadius: 14,
      background: highlight
        ? 'linear-gradient(135deg, rgba(212,175,55,0.10), rgba(194,157,82,0.04))'
        : 'rgba(255,255,255,0.03)',
      border: highlight
        ? '1px solid rgba(212,175,55,0.30)'
        : '1px solid rgba(255,255,255,0.08)',
    }}
  >
    <div style={{
      fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
      color: highlight ? '#D4AF37' : 'rgba(255,255,255,0.55)',
      marginBottom: 6,
    }}>{label}</div>
    <div style={{
      fontFamily: "Georgia, 'Times New Roman', serif",
      fontSize: 40, fontWeight: 400, lineHeight: 1.05, color: '#fff',
      letterSpacing: '-0.02em',
    }}>{value}</div>
    <div style={{
      marginTop: 4, fontSize: 12, color: 'rgba(255,255,255,0.55)',
    }}>{sub}</div>
    {note && (
      <div style={{
        marginTop: 6, fontSize: 11, color: note.tone,
        letterSpacing: '0.04em',
      }}>{note.label}</div>
    )}
  </div>
);
